import React, { useState } from "react";
import logo from "../../resource/7.png";
import useFormHandler from "../../Hook/useFormHandler";

const MembershipForFiveYear = () => {
  const initialValues = {
    // Personal Information
    first_name: "",
    last_name: "",
    date_of_birth: "",
    gender: "",
    
    // Address Information
    address: "",
    street_address: "",
    street_address_line_2: "",
    city: "",
    postal_code: "",
    country: "",
    
    // Contact Information
    whatsapp_number: "",
    mobile_number: "",
    email_address: "",
    
    // Donation Details
    donation_purpose: "",
    donation_purpose_other: "",
    donation_type: "",
    payment_method: "",
    
    // Signature and Date
    signature: "",
    application_date: "",
    
    ip_address: ""
  };

  const validations = {
    // Personal Information
    first_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "First Name should contain only letters, spaces, or hyphens",
    },
    last_name: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Last Name should contain only letters, spaces, or hyphens",
    },
    date_of_birth: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Date of Birth must be in YYYY-MM-DD format",
    },
    gender: {
      required: true,
      validate: (value) =>
        ["male", "female", "other"].includes(value) || "Gender must be Male, Female, or Other",
    },
    
    // Address Information
    address: {
      required: true,
      minLength: 5,
      message: "Address must be at least 5 characters long",
    },
    street_address: {
      required: true,
      minLength: 5,
      message: "Street address must be at least 5 characters long",
    },
    street_address_line_2: {
      required: false,
    },
    city: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "City should contain only letters, spaces, or hyphens",
    },
    postal_code: {
      required: true,
      pattern: /^\d{5,10}$/,
      message: "Postal Code should be 5 to 10 digits",
    },
    country: {
      required: true,
      pattern: /^[A-Za-z\s\-]+$/,
      message: "Country should contain only letters, spaces, or hyphens",
    },
    
    // Contact Information
    whatsapp_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "WhatsApp Number should be 10 to 15 digits, with optional '+'",
    },
    mobile_number: {
      required: true,
      pattern: /^\+?\d{10,15}$/,
      message: "Mobile Number should be 10 to 15 digits, with optional '+'",
    },
    email_address: {
      required: true,
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Invalid email format",
    },
    
    // Donation Details
    donation_purpose: {
      required: true,
      validate: (value) =>
        ["birthday", "wedding_anniversary", "punyanumoda", "other"].includes(value) || "Please select a donation purpose",
    },
    donation_purpose_other: {
      required: false,
      minLength: 3,
      message: "Please specify the purpose if 'Other' is selected",
    },
    donation_type: {
      required: true,
      validate: (value) =>
        ["individual", "group"].includes(value) || "Please select donation type",
    },
    payment_method: {
      required: true,
      validate: (value) =>
        ["online_payment", "monthly_direct_debit", "cash_deposit"].includes(value) || "Please select payment method",
    },
    
    // Signature and Date
    signature: {
      required: true,
      minLength: 3,
      message: "Signature must be at least 3 characters",
    },
    application_date: {
      required: true,
      pattern: /^\d{4}-\d{2}-\d{2}$/,
      message: "Application date must be in YYYY-MM-DD format",
    },
  };

  const endpoint = '/create-membership-requests';

  const transformForApi = (data) => ({
    // Personal Information
    first_name: data.first_name,
    last_name: data.last_name,
    date_of_birth: data.date_of_birth,
    gender: data.gender,
    
    // Address Information
    address: data.address,
    street_address: data.street_address,
    street_address_line_2: data.street_address_line_2,
    city: data.city,
    postal_code: data.postal_code,
    country: data.country,
    
    // Contact Information
    whatsapp_number: data.whatsapp_number,
    mobile_number: data.mobile_number,
    email_address: data.email_address,
    
    // Donation Details
    donation_purpose: data.donation_purpose === "other" ? data.donation_purpose_other : data.donation_purpose,
    donation_type: data.donation_type,
    payment_method: data.payment_method,
    
    // Signature and Date
    signature: data.signature,
    application_date: data.application_date,
    
    ip_address: data.ip_address,
  });

  const {
    formData,
    errors,
    loading,
    handleInputChange,
    handleSubmit,
    setFormData,
  } = useFormHandler({
    initialValues,
    validations,
    endpoint,
    includeIP: true,
    transformFn: transformForApi
  });

  return (
    <div className="form-page dark-gold">
      <form onSubmit={handleSubmit}>
        <div className="form-title">
          <img src={logo} alt="Monastery Logo" width={200} />
          <div className="details">
            <h1 className="font-second">Bhaddeka Vihari Monastery Center</h1>
            <h2 className="h4">Membership for the 5 Year Arama Pooja Project</h2>
          </div>
        </div>

        <p className="font-p mt-5">
          Welcome to the Bhaddheka Vihari Monastery Five Year Arama Pooja Membership Registration. 
          Kindly use the following form to provide your details.
        </p>
        
        <p className="font-p mt-5">Full Name*</p>
        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
            />
            <label htmlFor="first_name">First Name</label>
            {errors.first_name && <span className="error">{errors.first_name}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
            <label htmlFor="last_name">Last Name</label>
            {errors.last_name && <span className="error">{errors.last_name}</span>}
          </div>
        </div>

        <div className="form-box mt-4">
          <div className="form-item">
            <input
              type="date"
              id="date_of_birth"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleInputChange}
            />
            <label htmlFor="date_of_birth">Date of Birth*</label>
            {errors.date_of_birth && <span className="error">{errors.date_of_birth}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Gender*</p>
        <div className="gender-box">
          <div>
            <input
              type="radio"
              id="male"
              name="gender"
              value="male"
              checked={formData.gender === "male"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="male">Male</label>
          </div>
          <div>
            <input
              type="radio"
              id="female"
              name="gender"
              value="female"
              checked={formData.gender === "female"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="female">Female</label>
          </div>
          <div>
            <input
              type="radio"
              id="other"
              name="gender"
              value="other"
              checked={formData.gender === "other"}
              onChange={handleInputChange}
              className="mt-5"
            />
            <label htmlFor="other">Other</label>
          </div>
        </div>
        {errors.gender && <span className="error">{errors.gender}</span>}

        <p className="font-p mt-5">Address*</p>
        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
            />
            <label htmlFor="address">Full Address</label>
            {errors.address && <span className="error">{errors.address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Street Address*</p>
        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="text"
              id="street_address"
              name="street_address"
              value={formData.street_address}
              onChange={handleInputChange}
            />
            <label htmlFor="street_address">Street Address</label>
            {errors.street_address && <span className="error">{errors.street_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">Street Address Line 2</p>
        <div className="form-box1 mt-4">
          <div className="form-item">
            <input
              type="text"
              id="street_address_line_2"
              name="street_address_line_2"
              value={formData.street_address_line_2}
              onChange={handleInputChange}
            />
            <label htmlFor="street_address_line_2">Apartment, Unit, etc. (Optional)</label>
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
            />
            <label htmlFor="city">City*</label>
            {errors.city && <span className="error">{errors.city}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="postal_code"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleInputChange}
            />
            <label htmlFor="postal_code">Postal Code*</label>
            {errors.postal_code && <span className="error">{errors.postal_code}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
            />
            <label htmlFor="country">Country*</label>
            {errors.country && <span className="error">{errors.country}</span>}
          </div>
        </div>

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="whatsapp_number"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleInputChange}
            />
            <label htmlFor="whatsapp_number">WhatsApp Number*</label>
            {errors.whatsapp_number && <span className="error">{errors.whatsapp_number}</span>}
          </div>
          <div className="form-item">
            <input
              type="text"
              id="mobile_number"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleInputChange}
            />
            <label htmlFor="mobile_number">Mobile Phone Number* (including country code)</label>
            {errors.mobile_number && <span className="error">{errors.mobile_number}</span>}
          </div>
        </div>

        <div className="form-box1 mt-5">
          <div className="form-item">
            <input
              type="email"
              id="email_address"
              name="email_address"
              value={formData.email_address}
              onChange={handleInputChange}
            />
            <label htmlFor="email_address">Email Address*</label>
            {errors.email_address && <span className="error">{errors.email_address}</span>}
          </div>
        </div>

        <p className="font-p mt-5">
          Please indicate the purpose or event of significance for the 5 Year Arama Pooja Project Donation*
        </p>
        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="birthday">Birthday</label>
            <input
              type="radio"
              id="birthday"
              name="donation_purpose"
              value="birthday"
              checked={formData.donation_purpose === "birthday"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="wedding_anniversary">Wedding Anniversary</label>
            <input
              type="radio"
              id="wedding_anniversary"
              name="donation_purpose"
              value="wedding_anniversary"
              checked={formData.donation_purpose === "wedding_anniversary"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="punyanumoda">Punyanumoda (Deceased)</label>
            <input
              type="radio"
              id="punyanumoda"
              name="donation_purpose"
              value="punyanumoda"
              checked={formData.donation_purpose === "punyanumoda"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="other_purpose">Other</label>
            <input
              type="radio"
              id="other_purpose"
              name="donation_purpose"
              value="other"
              checked={formData.donation_purpose === "other"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.donation_purpose && <span className="error">{errors.donation_purpose}</span>}

        {formData.donation_purpose === "other" && (
          <div className="form-box1 mt-4">
            <div className="form-item">
              <input
                type="text"
                id="donation_purpose_other"
                name="donation_purpose_other"
                value={formData.donation_purpose_other}
                onChange={handleInputChange}
              />
              <label htmlFor="donation_purpose_other">Please specify other purpose</label>
              {errors.donation_purpose_other && <span className="error">{errors.donation_purpose_other}</span>}
            </div>
          </div>
        )}

        <p className="font-p mt-5">
          Please indicate if you are making this donation individually or as a group*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="individual">Individual</label>
            <input
              type="radio"
              id="individual"
              name="donation_type"
              value="individual"
              checked={formData.donation_type === "individual"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="group">Group</label>
            <input
              type="radio"
              id="group"
              name="donation_type"
              value="group"
              checked={formData.donation_type === "group"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.donation_type && <span className="error">{errors.donation_type}</span>}

        <p className="font-p mt-5">
          Please indicate how you wish to make your donation*
        </p>

        <div className="selection mt-4">
          <div className="checkbox-box">
            <label htmlFor="online_payment">Online Payment</label>
            <input
              type="radio"
              id="online_payment"
              name="payment_method"
              value="online_payment"
              checked={formData.payment_method === "online_payment"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="monthly_direct_debit">Monthly Direct Debit</label>
            <input
              type="radio"
              id="monthly_direct_debit"
              name="payment_method"
              value="monthly_direct_debit"
              checked={formData.payment_method === "monthly_direct_debit"}
              onChange={handleInputChange}
            />
          </div>
          <div className="checkbox-box">
            <label htmlFor="cash_deposit">Cash Deposit</label>
            <input
              type="radio"
              id="cash_deposit"
              name="payment_method"
              value="cash_deposit"
              checked={formData.payment_method === "cash_deposit"}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {errors.payment_method && <span className="error">{errors.payment_method}</span>}

        <div className="form-box mt-5">
          <div className="form-item">
            <input
              type="text"
              id="signature"
              name="signature"
              value={formData.signature}
              onChange={handleInputChange}
            />
            <label htmlFor="signature">Signature of the Applicant*</label>
            {errors.signature && <span className="error">{errors.signature}</span>}
          </div>
          <div className="form-item">
            <input
              type="date"
              id="application_date"
              name="application_date"
              value={formData.application_date}
              onChange={handleInputChange}
            />
            <label htmlFor="application_date">Date*</label>
            {errors.application_date && <span className="error">{errors.application_date}</span>}
          </div>
        </div>

        <button
          type="submit"
          id="submitbtn"
          className="mt-5"
          disabled={loading}
        >
          {loading ? "Submitting..." : "Submit"}
        </button>

        <p className="font-p mt-4">
          Thank you for your interest in becoming a member of our Arama Pooja community at the Bhaddheka Vihari Monastery.
        </p>
      </form>
    </div>
  );
};

export default MembershipForFiveYear;